import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  LayoutDashboard, Users, Video, FolderOpen, CreditCard, LogOut,
  Plus, Search, Upload, Trash2, BookOpen, GraduationCap, RefreshCw, Pencil,
  LibraryBig,
} from 'lucide-react';
import { ADMIN } from '@/constants/testIds';
import { BRAND } from '@/data/mockData';
import { useAuth } from '@/contexts/AuthContext';
import { api, formatApiError } from '@/lib/api';
import { CourseModal, BatchModal, EnrollmentModal, VideoUploadModal, ResourceUploadModal } from './AdminModals';
import { fmtDate, fmtPrice } from './_helpers';

const NAV = [
  { key: 'overview', label: 'Overview', icon: LayoutDashboard, tid: 'admin-nav-overview' },
  { key: 'courses', label: 'Courses', icon: BookOpen, tid: 'admin-nav-courses' },
  { key: 'batches', label: 'Batches', icon: BookOpen, tid: ADMIN.navBatches },
  { key: 'enrollments', label: 'Enrollments', icon: GraduationCap, tid: 'admin-nav-enrollments' },
  { key: 'videos', label: 'Videos', icon: Video, tid: ADMIN.navVideos },
  { key: 'resources', label: 'Resources', icon: FolderOpen, tid: ADMIN.navResources },
  { key: 'ebooks', label: 'eBook Library', icon: LibraryBig, tid: ADMIN.navEbooks },
  { key: 'students', label: 'Students', icon: Users, tid: ADMIN.navStudents },
  { key: 'payments', label: 'Payments', icon: CreditCard, tid: ADMIN.navPayments },
];

const AdminDashboard = () => {
  const [view, setView] = useState('overview');
  const [stats, setStats] = useState(null);
  const [courses, setCourses] = useState([]);
  const [batches, setBatches] = useState([]);
  const [students, setStudents] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const loadAll = useCallback(async () => {
    try {
      const [s, c, b, st, e] = await Promise.all([
        api.get('/admin/stats'),
        api.get('/admin/courses'),
        api.get('/admin/batches'),
        api.get('/admin/students'),
        api.get('/admin/enrollments'),
      ]);
      setStats(s.data);
      setCourses(c.data.items || []);
      setBatches(b.data.items || []);
      setStudents(st.data.items || []);
      setEnrollments(e.data.items || []);
    } catch (err) { toast.error(formatApiError(err)); }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const onLogout = async () => { await logout(); toast.success('Logged out'); navigate('/learning-hub'); };

  return (
    <div className="min-h-screen bg-[#050A14] text-white flex">
      <aside data-testid={ADMIN.sidebar} className="hidden lg:flex w-72 flex-col border-r border-white/5 bg-[#070d1c] sticky top-0 h-screen">
        <Link to="/" className="flex items-center gap-3 px-6 py-6 border-b border-white/5">
          <img src={BRAND.logoUrl} alt="BYMAT" className="h-10 w-10 object-contain rounded-lg bg-black" />
          <div>
            <div className="font-display font-black">BYMAT Admin</div>
            <div className="text-[10px] tracking-widest font-mono text-brand-cyan uppercase">Control Center</div>
          </div>
        </Link>
        {user && (
          <div className="px-4 py-3 border-b border-white/5">
            <div className="glass rounded-lg px-3 py-2">
              <div className="text-[10px] tracking-widest font-mono text-brand-cyan uppercase">Signed in</div>
              <div className="font-semibold text-sm">{user.full_name}</div>
              <div className="text-[11px] text-white/50 truncate">{user.email}</div>
            </div>
          </div>
        )}
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {NAV.map((n) => (
            <button key={n.key} type="button" data-testid={n.tid} onClick={() => setView(n.key)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition ${
                view === n.key ? 'bg-brand-cyan/10 text-brand-cyan border border-brand-cyan/20' : 'text-white/70 hover:bg-white/5'
              }`}>
              <n.icon className="w-4 h-4" />{n.label}
            </button>
          ))}
        </nav>
        <div className="p-4 border-t border-white/5">
          <button type="button" onClick={onLogout} data-testid={ADMIN.logoutButton}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-white/70 hover:bg-white/5">
            <LogOut className="w-4 h-4" /> Logout
          </button>
        </div>
      </aside>

      <div className="lg:hidden fixed top-0 inset-x-0 z-40 glass-strong border-b border-white/5 flex items-center justify-between px-4 h-14">
        <div className="flex items-center gap-2">
          <img src={BRAND.logoUrl} alt="BYMAT" className="h-8 w-8 object-contain rounded bg-black" />
          <div className="font-display font-bold text-sm">BYMAT Admin</div>
        </div>
        <button type="button" onClick={onLogout} className="text-xs text-white/70 inline-flex items-center gap-1.5"><LogOut className="w-3.5 h-3.5" /> Logout</button>
      </div>

      <main className="flex-1 min-w-0 pt-14 lg:pt-0">
        <div className="lg:hidden flex overflow-x-auto gap-2 px-4 py-3 border-b border-white/5">
          {NAV.map((n) => (
            <button key={n.key} type="button" onClick={() => setView(n.key)}
              className={`flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs ${view === n.key ? 'bg-brand-cyan text-black' : 'glass'}`}>
              <n.icon className="w-3.5 h-3.5" /> {n.label}
            </button>
          ))}
        </div>

        <div className="p-5 lg:p-8 space-y-6">
          <Header view={view} onRefresh={loadAll} />
          {view === 'overview' && <Overview stats={stats} batches={batches} enrollments={enrollments} />}
          {view === 'courses' && <CoursesView courses={courses} reload={loadAll} />}
          {view === 'batches' && <BatchesView batches={batches} courses={courses} reload={loadAll} />}
          {view === 'enrollments' && <EnrollmentsView enrollments={enrollments} students={students} batches={batches} reload={loadAll} />}
          {view === 'videos' && <VideosView batches={batches} />}
          {view === 'resources' && <ResourcesView batches={batches} />}
          {view === 'ebooks' && <EbooksView />}
          {view === 'students' && <StudentsView students={students} />}
          {view === 'payments' && <PaymentsView />}
        </div>
      </main>
    </div>
  );
};

const Header = ({ view, onRefresh }) => (
  <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
    <div>
      <div className="text-xs tracking-[0.3em] uppercase font-mono text-brand-cyan/80">Admin · {view}</div>
      <h1 className="font-display text-2xl sm:text-3xl font-black mt-1 capitalize">{view}</h1>
    </div>
    <div className="flex items-center gap-2">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
        <input placeholder="Search..." className="pl-9 pr-4 py-2 text-sm rounded-lg bg-white/5 border border-white/10 focus:border-brand-cyan/50 focus:outline-none" />
      </div>
      <button type="button" onClick={onRefresh} className="h-9 w-9 glass rounded-lg flex items-center justify-center text-white/70 hover:text-brand-cyan transition" aria-label="Refresh">
        <RefreshCw className="w-4 h-4" />
      </button>
    </div>
  </div>
);

const Card = ({ title, children, action }) => (
  <div className="glass rounded-2xl p-5">
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 gap-3">
      <div className="font-display font-bold">{title}</div>
      {action}
    </div>
    {children}
  </div>
);

const Overview = ({ stats, batches, enrollments }) => {
  const cards = [
    { l: 'Total Courses', v: stats?.total_courses ?? '…' },
    { l: 'Active Batches', v: stats?.active_batches ?? '…' },
    { l: 'Total Students', v: stats?.total_students ?? '…' },
    { l: 'Total Enrollments', v: stats?.total_enrollments ?? '…' },
  ];
  return (
    <>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((s) => (
          <div key={s.l} className="glass rounded-2xl p-5">
            <div className="text-[10px] tracking-widest uppercase font-mono text-white/50">{s.l}</div>
            <div className="font-display text-3xl font-black text-brand-cyan mt-2">{s.v}</div>
            <div className="text-xs text-white/50 mt-1">Live from PostgreSQL</div>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-2">
        <Card title="Recent Batches">
          {batches.length === 0 ? <Empty hint="No batches yet — create your first batch from the Batches tab." /> : (
            <ul className="divide-y divide-white/5">
              {batches.slice(0, 5).map((b) => (
                <li key={b.id} className="py-3 flex items-center justify-between">
                  <div>
                    <div className="font-semibold text-sm">{b.batch_name}</div>
                    <div className="text-xs text-white/50">{b.trainer_name || '—'} · starts {fmtDate(b.start_date)}</div>
                  </div>
                  <span className={`text-[11px] px-2 py-0.5 rounded-full ${b.is_active ? 'bg-emerald-500/10 text-emerald-400' : 'bg-amber-500/10 text-amber-400'}`}>{b.is_active ? 'Active' : 'Inactive'}</span>
                </li>
              ))}
            </ul>
          )}
        </Card>
        <Card title="Recent Enrollments">
          {enrollments.length === 0 ? <Empty hint="No enrollments yet — enroll a student from the Enrollments tab." /> : (
            <ul className="divide-y divide-white/5">
              {enrollments.slice(0, 5).map((e) => (
                <li key={e.id} className="py-3 flex items-center justify-between">
                  <div>
                    <div className="font-semibold text-sm">{e.student?.full_name || '—'}</div>
                    <div className="text-xs text-white/50">{e.batch?.batch_name || '—'}</div>
                  </div>
                  <span className="text-[11px] px-2 py-0.5 rounded-full bg-brand-cyan/10 text-brand-cyan">{e.payment_status}</span>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </>
  );
};

const CoursesView = ({ courses, reload }) => {
  const [open, setOpen] = useState(false);
  const onDelete = async (id) => {
    if (!window.confirm('Soft-delete (deactivate) this course?')) return;
    try { await api.delete(`/admin/courses/${id}`); toast.success('Course deactivated'); reload(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <Card title={`Courses · ${courses.length}`} action={
      <button type="button" data-testid="admin-create-course-button" onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1.5 bg-brand-cyan text-black text-xs font-semibold px-3 py-1.5 rounded-md btn-glow">
        <Plus className="w-3.5 h-3.5" /> Create course
      </button>
    }>
      {courses.length === 0 ? <Empty hint="No courses yet." /> : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] tracking-widest uppercase font-mono text-white/50 border-b border-white/5">
                <th className="py-2 pr-4">Course</th><th className="py-2 pr-4">Slug</th>
                <th className="py-2 pr-4">Duration</th><th className="py-2 pr-4">Price</th>
                <th className="py-2 pr-4">Status</th><th className="py-2"></th>
              </tr>
            </thead>
            <tbody>
              {courses.map((c) => (
                <tr key={c.id} data-testid={`course-row-${c.slug}`} className="border-b border-white/5">
                  <td className="py-3 pr-4 font-semibold">{c.course_name}</td>
                  <td className="py-3 pr-4 text-brand-cyan/80 font-mono text-xs">{c.slug}</td>
                  <td className="py-3 pr-4 text-white/70">{c.duration || '—'}</td>
                  <td className="py-3 pr-4 text-white/70">{fmtPrice(c.price)}</td>
                  <td className="py-3 pr-4"><span className={`text-[11px] px-2 py-0.5 rounded-full ${c.is_active ? 'bg-emerald-500/10 text-emerald-400' : 'bg-white/10 text-white/60'}`}>{c.is_active ? 'Active' : 'Inactive'}</span></td>
                  <td className="py-3 text-right">
                    <button type="button" onClick={() => onDelete(c.id)} className="text-red-400/80 hover:text-red-400 inline-flex items-center gap-1.5 text-xs">
                      <Trash2 className="w-3.5 h-3.5" /> Deactivate
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <CourseModal open={open} onOpenChange={setOpen} onCreated={reload} />
    </Card>
  );
};

const BatchesView = ({ batches, courses, reload }) => {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const courseName = (id) => courses.find((c) => c.id === id)?.course_name || '—';
  const onDelete = async (id) => {
    if (!window.confirm('Delete this batch? Enrollments will be removed too.')) return;
    try { await api.delete(`/admin/batches/${id}`); toast.success('Batch deleted'); reload(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <Card title={`Batches · ${batches.length}`} action={
      <button type="button" data-testid={ADMIN.createBatchButton} onClick={() => { setEditing(null); setOpen(true); }}
        className="inline-flex items-center gap-1.5 bg-brand-cyan text-black text-xs font-semibold px-3 py-1.5 rounded-md btn-glow">
        <Plus className="w-3.5 h-3.5" /> Create batch
      </button>
    }>
      {batches.length === 0 ? <Empty hint="No batches yet — pick a course and create one." /> : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] tracking-widest uppercase font-mono text-white/50 border-b border-white/5">
                <th className="py-2 pr-4">Batch</th><th className="py-2 pr-4">Course</th>
                <th className="py-2 pr-4">Fee</th><th className="py-2 pr-4">Payment</th>
                <th className="py-2 pr-4">Start</th><th className="py-2 pr-4">Trainer</th>
                <th className="py-2 pr-4">Status</th><th className="py-2"></th>
              </tr>
            </thead>
            <tbody>
              {batches.map((b) => (
                <tr key={b.id} data-testid={`batch-row-${b.id}`} className="border-b border-white/5">
                  <td className="py-3 pr-4 font-semibold">{b.batch_name}</td>
                  <td className="py-3 pr-4 text-white/70">{courseName(b.course_id)}</td>
                  <td className="py-3 pr-4 text-white/80 font-mono text-xs whitespace-nowrap" data-testid={`batch-fee-${b.id}`}>
                    {b.course_fee != null
                      ? `₹${Number(b.course_fee).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                      : '—'}
                  </td>
                  <td className="py-3 pr-4">
                    {b.payment_link
                      ? <a href={b.payment_link} target="_blank" rel="noopener noreferrer" data-testid={`batch-payment-link-${b.id}`} className="text-brand-cyan hover:underline text-xs">Open Payment Link</a>
                      : <span className="text-white/40 text-xs">—</span>}
                  </td>
                  <td className="py-3 pr-4 text-white/70 font-mono text-xs">{fmtDate(b.start_date)}</td>
                  <td className="py-3 pr-4 text-white/70">{b.trainer_name || '—'}</td>
                  <td className="py-3 pr-4"><span className={`text-[11px] px-2 py-0.5 rounded-full ${b.is_active ? 'bg-emerald-500/10 text-emerald-400' : 'bg-white/10 text-white/60'}`}>{b.is_active ? 'Active' : 'Inactive'}</span></td>
                  <td className="py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button type="button" onClick={() => { setEditing(b); setOpen(true); }} className="text-white/60 hover:text-brand-cyan inline-flex items-center gap-1 text-xs"><Pencil className="w-3.5 h-3.5" />Edit</button>
                      <button type="button" onClick={() => onDelete(b.id)} className="text-red-400/80 hover:text-red-400 inline-flex items-center gap-1 text-xs"><Trash2 className="w-3.5 h-3.5" />Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <BatchModal open={open} onOpenChange={setOpen} courses={courses} onCreated={reload} initial={editing} />
    </Card>
  );
};

const EnrollmentsView = ({ enrollments, students, batches, reload }) => {
  const [open, setOpen] = useState(false);
  const onDelete = async (id) => {
    if (!window.confirm('Remove this enrollment?')) return;
    try { await api.delete(`/admin/enrollments/${id}`); toast.success('Enrollment removed'); reload(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <Card title={`Enrollments · ${enrollments.length}`} action={
      <button type="button" data-testid="admin-create-enrollment-button" onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1.5 bg-brand-cyan text-black text-xs font-semibold px-3 py-1.5 rounded-md btn-glow">
        <Plus className="w-3.5 h-3.5" /> Enroll student
      </button>
    }>
      {enrollments.length === 0 ? <Empty hint="No enrollments yet. Pick a student + batch and assign access." /> : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] tracking-widest uppercase font-mono text-white/50 border-b border-white/5">
                <th className="py-2 pr-4">Student</th><th className="py-2 pr-4">Batch</th>
                <th className="py-2 pr-4">Enrolled</th><th className="py-2 pr-4">Payment</th>
                <th className="py-2 pr-4">Access</th><th className="py-2"></th>
              </tr>
            </thead>
            <tbody>
              {enrollments.map((e) => (
                <tr key={e.id} data-testid={`enrollment-row-${e.id}`} className="border-b border-white/5">
                  <td className="py-3 pr-4">
                    <div className="font-semibold">{e.student?.full_name || '—'}</div>
                    <div className="text-xs text-white/50">{e.student?.email || ''}</div>
                  </td>
                  <td className="py-3 pr-4 text-white/70">{e.batch?.batch_name || '—'}</td>
                  <td className="py-3 pr-4 text-white/70 font-mono text-xs">{fmtDate(e.enrollment_date)}</td>
                  <td className="py-3 pr-4"><span className="text-[11px] px-2 py-0.5 rounded-full bg-brand-cyan/10 text-brand-cyan">{e.payment_status}</span></td>
                  <td className="py-3 pr-4"><span className={`text-[11px] px-2 py-0.5 rounded-full ${e.access_status === 'active' ? 'bg-emerald-500/10 text-emerald-400' : 'bg-amber-500/10 text-amber-400'}`}>{e.access_status}</span></td>
                  <td className="py-3 text-right">
                    <button type="button" onClick={() => onDelete(e.id)} className="text-red-400/80 hover:text-red-400 inline-flex items-center gap-1 text-xs"><Trash2 className="w-3.5 h-3.5" />Remove</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <EnrollmentModal open={open} onOpenChange={setOpen} students={students} batches={batches.filter((b) => b.is_active)} onCreated={reload} />
    </Card>
  );
};

const StudentsView = ({ students }) => (
  <Card title={`Students · ${students.length}`}>
    {students.length === 0 ? <Empty hint="No registered students yet. Students self-register from /learning-hub." /> : (
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[11px] tracking-widest uppercase font-mono text-white/50 border-b border-white/5">
              <th className="py-2 pr-4">Name</th><th className="py-2 pr-4">Email</th>
              <th className="py-2 pr-4">Mobile</th><th className="py-2 pr-4">Joined</th>
            </tr>
          </thead>
          <tbody>
            {students.map((s) => (
              <tr key={s.id} data-testid={`student-row-${s.id}`} className="border-b border-white/5">
                <td className="py-3 pr-4 font-semibold">{s.full_name}</td>
                <td className="py-3 pr-4 text-white/70">{s.email}</td>
                <td className="py-3 pr-4 text-white/70">{s.mobile || '—'}</td>
                <td className="py-3 pr-4 text-white/70 font-mono text-xs">{fmtDate(s.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </Card>
);

const VideosView = ({ batches }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const batchName = (id) => batches.find((b) => b.id === id)?.batch_name || '—';
  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get('/admin/videos'); setItems(data.items || []); }
    catch (e) { /* ignore */ } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const onDelete = async (id) => {
    if (!window.confirm('Delete this video from Bunny Stream + database?')) return;
    try { await api.delete(`/admin/videos/${id}`); toast.success('Video deleted'); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <Card title={`Session Recordings · ${items.length}`} action={
      <button type="button" data-testid="admin-upload-video-button" onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1.5 bg-brand-cyan text-black text-xs font-semibold px-3 py-1.5 rounded-md btn-glow">
        <Upload className="w-3.5 h-3.5" /> Upload video
      </button>
    }>
      {loading ? <Empty hint="Loading..." /> : items.length === 0 ? <Empty hint="No recordings yet. Click 'Upload video' to push your first session to Bunny Stream." /> : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] tracking-widest uppercase font-mono text-white/50 border-b border-white/5">
                <th className="py-2 pr-4">Title</th><th className="py-2 pr-4">Batch</th>
                <th className="py-2 pr-4">Status</th><th className="py-2 pr-4">Date</th><th className="py-2"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((v) => (
                <tr key={v.id} className="border-b border-white/5">
                  <td className="py-3 pr-4 font-semibold">{v.title}</td>
                  <td className="py-3 pr-4 text-white/70">{batchName(v.batch_id)}</td>
                  <td className="py-3 pr-4"><span className="text-[11px] px-2 py-0.5 rounded-full bg-brand-cyan/10 text-brand-cyan font-mono">{v.video_status || 'processing'}</span></td>
                  <td className="py-3 pr-4 text-white/70 font-mono text-xs">{v.session_date ? v.session_date.slice(0,10) : '—'}</td>
                  <td className="py-3 text-right">
                    <button type="button" onClick={() => onDelete(v.id)} className="text-red-400/80 hover:text-red-400 inline-flex items-center gap-1 text-xs"><Trash2 className="w-3.5 h-3.5" />Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <VideoUploadModal open={open} onOpenChange={setOpen} batches={batches.filter((b) => b.is_active)} onUploaded={load} />
    </Card>
  );
};

const ResourcesView = ({ batches }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const batchName = (id) => batches.find((b) => b.id === id)?.batch_name || '—';
  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get('/admin/resources'); setItems(data.items || []); }
    catch (e) { /* ignore */ } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const onDelete = async (id) => {
    if (!window.confirm('Delete this file from R2 + database?')) return;
    try { await api.delete(`/admin/resources/${id}`); toast.success('Resource deleted'); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <Card title={`Downloadable Resources · ${items.length}`} action={
      <button type="button" data-testid="admin-upload-resource-button" onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1.5 bg-brand-cyan text-black text-xs font-semibold px-3 py-1.5 rounded-md btn-glow">
        <Upload className="w-3.5 h-3.5" /> Upload file
      </button>
    }>
      {loading ? <Empty hint="Loading..." /> : items.length === 0 ? <Empty hint="No files yet. Click 'Upload file' to push PDFs / DOCX / ZIPs to Cloudflare R2." /> : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] tracking-widest uppercase font-mono text-white/50 border-b border-white/5">
                <th className="py-2 pr-4">Title</th><th className="py-2 pr-4">Batch</th>
                <th className="py-2 pr-4">Type</th><th className="py-2 pr-4">Size</th>
                <th className="py-2 pr-4">Download</th><th className="py-2"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((r) => (
                <tr key={r.id} className="border-b border-white/5">
                  <td className="py-3 pr-4 font-semibold">{r.title}</td>
                  <td className="py-3 pr-4 text-white/70">{batchName(r.batch_id)}</td>
                  <td className="py-3 pr-4 text-brand-cyan font-mono text-xs">{(r.file_type || '').split('/').pop() || '—'}</td>
                  <td className="py-3 pr-4 text-white/70">{r.file_size || '—'}</td>
                  <td className="py-3 pr-4"><span className={`text-[11px] px-2 py-0.5 rounded-full ${r.is_downloadable ? 'bg-emerald-500/10 text-emerald-400' : 'bg-red-500/10 text-red-400'}`}>{r.is_downloadable ? 'Enabled' : 'Locked'}</span></td>
                  <td className="py-3 text-right">
                    <button type="button" onClick={() => onDelete(r.id)} className="text-red-400/80 hover:text-red-400 inline-flex items-center gap-1 text-xs"><Trash2 className="w-3.5 h-3.5" />Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <ResourceUploadModal open={open} onOpenChange={setOpen} batches={batches.filter((b) => b.is_active)} onUploaded={load} />
    </Card>
  );
};

const IntegrationListView = ({ title, endpoint, deleteEndpoint, emptyHint }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    try {
      const { data } = await api.get(endpoint);
      setItems(data.items || []);
    } catch (e) { /* ignore */ } finally { setLoading(false); }
  }, [endpoint]);
  useEffect(() => { load(); }, [load]);
  const onDelete = async (id) => {
    if (!window.confirm('Delete this item?')) return;
    try { await api.delete(`${deleteEndpoint}/${id}`); toast.success('Deleted'); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <Card title={`${title} · ${items.length}`} action={
      <button type="button" onClick={load} className="inline-flex items-center gap-1.5 glass text-xs font-semibold px-3 py-1.5 rounded-md hover:bg-brand-cyan/10 hover:text-brand-cyan">
        <RefreshCw className="w-3.5 h-3.5" /> Refresh
      </button>
    }>
      {loading ? <Empty hint="Loading..." /> : items.length === 0 ? <Empty hint={emptyHint} /> : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] tracking-widest uppercase font-mono text-white/50 border-b border-white/5">
                <th className="py-2 pr-4">Title</th>
                <th className="py-2 pr-4">Type / Status</th>
                <th className="py-2 pr-4">Created</th>
                <th className="py-2"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.id} className="border-b border-white/5">
                  <td className="py-3 pr-4 font-semibold">{it.title}</td>
                  <td className="py-3 pr-4 text-white/70 text-xs font-mono">{it.video_status || it.file_type || '—'}</td>
                  <td className="py-3 pr-4 text-white/70 font-mono text-xs">{it.created_at ? it.created_at.slice(0,10) : (it.session_date || '—').slice(0,10)}</td>
                  <td className="py-3 text-right">
                    <button type="button" onClick={() => onDelete(it.id)} className="text-red-400/80 hover:text-red-400 inline-flex items-center gap-1 text-xs"><Trash2 className="w-3.5 h-3.5" />Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
};

const PaymentsView = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get('/admin/payments').then((r) => setItems(r.data.items || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);
  return (
    <Card title={`Razorpay Payments · ${items.length}`}>
      {loading ? <Empty hint="Loading..." /> : items.length === 0 ? <Empty hint="No payments yet. When a student completes Razorpay checkout, transactions appear here with auto-enrollment." /> : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] tracking-widest uppercase font-mono text-white/50 border-b border-white/5">
                <th className="py-2 pr-4">Order ID</th>
                <th className="py-2 pr-4">Student</th>
                <th className="py-2 pr-4">Amount</th>
                <th className="py-2 pr-4">Status</th>
                <th className="py-2 pr-4">Date</th>
              </tr>
            </thead>
            <tbody>
              {items.map((p) => (
                <tr key={p.id} className="border-b border-white/5">
                  <td className="py-3 pr-4 font-mono text-xs">{p.razorpay_order_id || '—'}</td>
                  <td className="py-3 pr-4">{p.student?.full_name || '—'}<div className="text-xs text-white/50">{p.student?.email || ''}</div></td>
                  <td className="py-3 pr-4">₹{p.amount}</td>
                  <td className="py-3 pr-4"><span className={`text-[11px] px-2 py-0.5 rounded-full ${p.status === 'captured' ? 'bg-emerald-500/10 text-emerald-400' : 'bg-amber-500/10 text-amber-400'}`}>{p.status || 'created'}</span></td>
                  <td className="py-3 pr-4 font-mono text-xs">{p.payment_date ? p.payment_date.slice(0,10) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
};

const EbooksView = () => {
  const inputClass = 'w-full px-3 py-2.5 text-sm rounded-lg bg-white/5 border border-white/10 text-white caret-brand-cyan focus:border-brand-cyan/60 focus:outline-none placeholder:text-white/35';
  const selectClass = `${inputClass} [&>option]:bg-[#0B1221] [&>option]:text-white`;
  const [ebooks, setEbooks] = useState([]);
  const [bundles, setBundles] = useState([]);
  const emptyBook = { title: '', description: '', author: '', category: '', price: '0', original_price: '', is_published: false };
  const emptyBundle = { title: '', description: '', price: '0', original_price: '', is_published: false, ebook_ids: [] };
  const [form, setForm] = useState(emptyBook);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState(emptyBook);
  const [file, setFile] = useState(null);
  const [cover, setCover] = useState(null);
  const [bundleForm, setBundleForm] = useState(emptyBundle);
  const [bundleCover, setBundleCover] = useState(null);
  const [editingBundle, setEditingBundle] = useState(null);
  const [saving, setSaving] = useState(false);
  const load = useCallback(async () => {
    try {
      const [ebookResponse, bundleResponse] = await Promise.all([api.get('/admin/ebooks'), api.get('/admin/ebook-bundles')]);
      setEbooks(ebookResponse.data.items || []); setBundles(bundleResponse.data.items || []);
    } catch (err) { toast.error(formatApiError(err)); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const upload = async (event) => {
    event.preventDefault();
    if (!file) { toast.error('Select an eBook file'); return; }
    setSaving(true);
    try {
      const data = new FormData();
      Object.entries(form).forEach(([key, value]) => data.append(key, value));
      data.append('file', file); if (cover) data.append('cover', cover);
      await api.post('/admin/ebooks/upload', data, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('eBook uploaded'); setForm(emptyBook); setFile(null); setCover(null); load();
    } catch (err) { toast.error(formatApiError(err)); } finally { setSaving(false); }
  };
  const togglePublish = async (book) => { try { await api.patch(`/admin/ebooks/${book.id}`, { is_published: !book.is_published }); load(); } catch (err) { toast.error(formatApiError(err)); } };
  const remove = async (id) => { if (!window.confirm('Delete this eBook and remove its stored files?')) return; try { await api.delete(`/admin/ebooks/${id}`); toast.success('eBook deleted'); load(); } catch (err) { toast.error(formatApiError(err)); } };
  const startEdit = (book) => { setEditing(book.id); setEditForm({ ...emptyBook, ...book }); };
  const saveEdit = async (event) => { event.preventDefault(); try { await api.patch(`/admin/ebooks/${editing}`, { ...editForm, price: Number(editForm.price), original_price: editForm.original_price === '' ? null : Number(editForm.original_price) }); toast.success('eBook updated'); setEditing(null); load(); } catch (err) { toast.error(formatApiError(err)); } };
  const replaceFiles = async (event) => { event.preventDefault(); if (!file && !cover) { toast.error('Select a replacement file or cover'); return; } try { const data = new FormData(); if (file) data.append('file', file); if (cover) data.append('cover', cover); await api.post(`/admin/ebooks/${editing}/files`, data, { headers: { 'Content-Type': 'multipart/form-data' } }); toast.success('eBook files replaced'); setFile(null); setCover(null); load(); } catch (err) { toast.error(formatApiError(err)); } };
  const saveBundle = async (event) => { event.preventDefault(); try { const payload = { ...bundleForm, price: Number(bundleForm.price), original_price: bundleForm.original_price === '' ? null : Number(bundleForm.original_price), ebook_ids: bundleForm.ebook_ids }; const path = editingBundle ? `/admin/ebook-bundles/${editingBundle}` : '/admin/ebook-bundles'; const { data: saved } = await api[editingBundle ? 'patch' : 'post'](path, payload); if (bundleCover) { const coverData = new FormData(); coverData.append('cover', bundleCover); await api.post(`/admin/ebook-bundles/${saved.id}/cover`, coverData, { headers: { 'Content-Type': 'multipart/form-data' } }); } toast.success(editingBundle ? 'Bundle updated' : 'Bundle created'); setBundleForm(emptyBundle); setBundleCover(null); setEditingBundle(null); load(); } catch (err) { toast.error(formatApiError(err)); } };
  const removeBundle = async (id) => { if (!window.confirm('Delete this bundle?')) return; try { await api.delete(`/admin/ebook-bundles/${id}`); toast.success('Bundle deleted'); load(); } catch (err) { toast.error(formatApiError(err)); } };
  return <div className="space-y-6">
    <Card title="Upload eBook">
      <form onSubmit={upload} className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Title" className={inputClass} />
        <input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Description" className={inputClass} />
        <input value={form.author} onChange={(e) => setForm({ ...form, author: e.target.value })} placeholder="Author" className={inputClass} />
        <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value, price: e.target.value === 'Free' ? '0' : form.price })} className={selectClass} aria-label="eBook category">
          <option value="">Select category</option>
          <option value="Free">Free</option>
          <option value="Premium">Premium</option>
        </select>
        <input type="number" min="0" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} placeholder="Price (INR)" className={inputClass} />
        <input type="number" min="0" step="0.01" value={form.original_price} onChange={(e) => setForm({ ...form, original_price: e.target.value })} placeholder="Original price (optional)" className={inputClass} />
        <label className="text-xs text-white/60">eBook file<input required type="file" onChange={(e) => setFile(e.target.files[0])} className="block mt-2 text-sm text-white file:mr-3 file:rounded-md file:border-0 file:bg-brand-cyan file:px-3 file:py-1.5 file:text-black" /></label>
        <label className="text-xs text-white/60">Cover image<input type="file" accept="image/*" onChange={(e) => setCover(e.target.files[0])} className="block mt-2 text-sm text-white file:mr-3 file:rounded-md file:border-0 file:bg-brand-cyan file:px-3 file:py-1.5 file:text-black" /></label>
        <label className="inline-flex items-center gap-2 text-sm"><input type="checkbox" checked={form.is_published} onChange={(e) => setForm({ ...form, is_published: e.target.checked })} /> Publish immediately</label>
        <button disabled={saving} className="bg-brand-cyan text-black rounded-lg px-4 py-2 font-semibold text-sm disabled:opacity-50">{saving ? 'Uploading...' : 'Upload eBook'}</button>
      </form>
    </Card>
    <Card title={`eBooks · ${ebooks.length}`} action={<button type="button" onClick={load} className="glass px-3 py-1.5 rounded-md text-xs">Refresh</button>}>
      {ebooks.length === 0 ? <Empty hint="No eBooks yet." /> : <div className="overflow-x-auto"><table className="w-full text-sm"><tbody>{ebooks.map((book) => <tr key={book.id} className="border-b border-white/5"><td className="py-3 pr-4 font-semibold">{book.title}<div className="text-xs text-white/50">{book.is_free ? 'Free' : `INR ${book.price}`} · {book.author || 'Unknown author'}</div></td><td className="py-3 pr-4 text-xs">{book.is_published ? 'Published' : 'Draft'}</td><td className="py-3 text-right space-x-3"><button type="button" onClick={() => startEdit(book)} className="text-white/70 text-xs">Edit</button><button type="button" onClick={() => togglePublish(book)} className="text-brand-cyan text-xs">{book.is_published ? 'Unpublish' : 'Publish'}</button><button type="button" onClick={() => remove(book.id)} className="text-red-400 text-xs">Delete</button></td></tr>)}</tbody></table></div>}
      {editing && <div className="mt-5 border-t border-white/10 pt-5"><form onSubmit={saveEdit} className="grid grid-cols-1 md:grid-cols-2 gap-3"><input required value={editForm.title} onChange={(e) => setEditForm({ ...editForm, title: e.target.value })} placeholder="Title" className={inputClass} /><input value={editForm.description || ''} onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} placeholder="Description" className={inputClass} /><input value={editForm.author || ''} onChange={(e) => setEditForm({ ...editForm, author: e.target.value })} placeholder="Author" className={inputClass} /><select value={editForm.category || ''} onChange={(e) => setEditForm({ ...editForm, category: e.target.value, price: e.target.value === 'Free' ? '0' : editForm.price })} className={selectClass} aria-label="Edit eBook category"><option value="">Select category</option><option value="Free">Free</option><option value="Premium">Premium</option></select><input type="number" min="0" step="0.01" value={editForm.price} onChange={(e) => setEditForm({ ...editForm, price: e.target.value })} placeholder="Price (INR)" className={inputClass} /><input type="number" min="0" step="0.01" value={editForm.original_price || ''} onChange={(e) => setEditForm({ ...editForm, original_price: e.target.value })} placeholder="Original price" className={inputClass} /><label className="inline-flex items-center gap-2 text-sm"><input type="checkbox" checked={!!editForm.is_published} onChange={(e) => setEditForm({ ...editForm, is_published: e.target.checked })} /> Published</label><div className="flex gap-2"><button className="bg-brand-cyan text-black rounded-lg px-4 py-2 text-sm font-semibold">Save metadata</button><button type="button" onClick={() => setEditing(null)} className="glass rounded-lg px-4 py-2 text-sm">Cancel</button></div></form><form onSubmit={replaceFiles} className="mt-4 flex flex-wrap items-end gap-3"><label className="text-xs text-white/60">Replace book<input required={!cover} type="file" accept=".pdf,.epub,application/pdf,application/epub+zip" onChange={(e) => setFile(e.target.files[0])} className="block mt-2 text-sm text-white file:mr-3 file:rounded-md file:border-0 file:bg-brand-cyan file:px-3 file:py-1.5 file:text-black" /></label><label className="text-xs text-white/60">Replace cover<input required={!file} type="file" accept="image/*" onChange={(e) => setCover(e.target.files[0])} className="block mt-2 text-sm text-white file:mr-3 file:rounded-md file:border-0 file:bg-brand-cyan file:px-3 file:py-1.5 file:text-black" /></label><button className="bg-brand-cyan text-black rounded-lg px-4 py-2 text-sm font-semibold">Replace files</button></form></div>}
    </Card>
    <Card title={`Bundles · ${bundles.length}`}>
      <form onSubmit={saveBundle} className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-5">
        <input required value={bundleForm.title} onChange={(e) => setBundleForm({ ...bundleForm, title: e.target.value })} placeholder="Bundle title" className={inputClass} />
        <input value={bundleForm.description} onChange={(e) => setBundleForm({ ...bundleForm, description: e.target.value })} placeholder="Description" className={inputClass} />
        <input required type="number" min="0" step="0.01" value={bundleForm.price} onChange={(e) => setBundleForm({ ...bundleForm, price: e.target.value })} placeholder="Bundle price (INR)" className={inputClass} />
        <input type="number" min="0" step="0.01" value={bundleForm.original_price || ''} onChange={(e) => setBundleForm({ ...bundleForm, original_price: e.target.value })} placeholder="Original price (optional)" className={inputClass} />
        <label className="text-xs text-white/60">Bundle cover image<input type="file" accept="image/*" onChange={(e) => setBundleCover(e.target.files?.[0] || null)} className="block mt-2 text-sm text-white file:mr-3 file:rounded-md file:border-0 file:bg-brand-cyan file:px-3 file:py-1.5 file:text-black" /></label>
        <label className="inline-flex items-center gap-2 text-sm"><input type="checkbox" checked={!!bundleForm.is_published} onChange={(e) => setBundleForm({ ...bundleForm, is_published: e.target.checked })} /> Published</label>
        <select multiple value={bundleForm.ebook_ids} onChange={(e) => setBundleForm({ ...bundleForm, ebook_ids: Array.from(e.target.selectedOptions, (option) => option.value) })} className={`${selectClass} min-h-24`}><option disabled value="">Select included eBooks</option>{ebooks.map((book) => <option key={book.id} value={book.id}>{book.title}</option>)}</select>
        <div className="flex gap-2"><button className="bg-brand-cyan text-black rounded-lg px-4 py-2 font-semibold text-sm">{editingBundle ? 'Save Bundle' : 'Create Bundle'}</button>{editingBundle && <button type="button" onClick={() => { setEditingBundle(null); setBundleForm(emptyBundle); }} className="glass rounded-lg px-4 py-2 text-sm">Cancel</button>}</div>
      </form>
      {bundles.length === 0 ? <Empty hint="No bundles yet." /> : <div className="space-y-2">{bundles.map((bundle) => <div key={bundle.id} className="flex items-center justify-between border-b border-white/5 py-3"><div><div className="font-semibold">{bundle.title}</div><div className="text-xs text-white/50">INR {bundle.price} · {bundle.items?.length || 0} eBooks</div></div><div className="flex items-center gap-3"><span className="text-xs text-white/60">{bundle.is_published ? 'Published' : 'Draft'}</span><button type="button" onClick={() => { setEditingBundle(bundle.id); setBundleCover(null); setBundleForm({ ...emptyBundle, ...bundle, ebook_ids: (bundle.items || []).map((item) => item.id) }); }} className="text-white/70 text-xs">Edit</button><button type="button" onClick={() => removeBundle(bundle.id)} className="text-red-400 text-xs">Delete</button></div></div>)}</div>}
    </Card>
  </div>;
};

const Empty = ({ hint }) => (
  <div className="rounded-xl border border-dashed border-white/10 p-8 text-center">
    <div className="text-sm text-white/60">{hint}</div>
  </div>
);

export default AdminDashboard;
